import React from 'react';

import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import StructuredTableEditStatusManager from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableEditStatusManager';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';

interface ToggleEditStructuredTableOutsideNotebookProps<T extends UnknownStructuredTable> {
  isEditable: boolean;
  structuredTable: T;
  isMaximized?: boolean;
  disableMaximizedTables?: boolean;
  outsideNotebookContext?: OutsideNotebookContext;
  update: (structuredTable: T) => void;
  updateComputedTable?: (computedTable: Array<Array<EvaluatedCell>>) => void;
  setIsMaximized?: () => void;
  submit: () => Promise<void>;
  revert: (structuredTable: T) => void;
  enableFormulas?: boolean;
}

export default function ToggleEditStructuredTableOutsideNotebook<T extends UnknownStructuredTable>(
  props: ToggleEditStructuredTableOutsideNotebookProps<T>
): JSX.Element {
  return props.isEditable ? (
    <StructuredTableEditStatusManager
      tableCid={props.structuredTable.cid}
      table={props.structuredTable}
      submitHandler={props.submit}
      revertHandler={props.revert}
    >
      {(editStatus: StructuredTableEditStatus) => (
        <StructuredTableOutsideNotebook
          structuredTable={props.structuredTable}
          cid={props.structuredTable.cid}
          updateHandler={props.update}
          onComputedTableChanged={props.updateComputedTable}
          disableMaximizedTables={props.disableMaximizedTables ?? false}
          isMaximized={props.isMaximized}
          setMaximizedTable={props.setIsMaximized}
          outsideNotebookContext={props.outsideNotebookContext}
          locked={editStatus === StructuredTableEditStatus.LOCKED}
          hideFormulaBox={editStatus === StructuredTableEditStatus.LOCKED}
          includeUnsubmittedStatus={editStatus === StructuredTableEditStatus.EDITING}
          enableFormulas={props.enableFormulas}
        />
      )}
    </StructuredTableEditStatusManager>
  ) : (
    <StructuredTableOutsideNotebook
      structuredTable={props.structuredTable}
      cid={props.structuredTable.cid}
      // A non-editable table can still be updated (e.g., by action functions)
      updateHandler={props.update}
      locked={true}
      hideFormulaBox={true}
      disableMaximizedTables={props.disableMaximizedTables ?? false}
      isMaximized={props.isMaximized}
      setMaximizedTable={props.setIsMaximized}
      includeUnsubmittedStatus={false}
      enableFormulas={props.enableFormulas}
    />
  );
}
